import type { DashboardFilters, DashboardIndicators, ProgressBar } from "./dashboard-indicators.js";
import type { InspectorPerformance, InspectorPerformanceReport } from "./inspector-performance.js";

/**
 * LINK COMPARTILHADO DO PAINEL DE INDICADORES E AVANÇOS (Correção 9).
 *
 * Um terceiro abre o Painel por um link, sem conta no sistema, em modo
 * SOMENTE LEITURA. Este módulo guarda as regras puras:
 *
 *  - o link é um TOKEN aleatório de 256 bits — nunca `?projectId=1&public=true`,
 *    que qualquer um enumeraria;
 *  - validade, revogação e o estado que decorre delas;
 *  - o ESCOPO gravado no link e o recorte que o visitante pode fazer dentro
 *    dele (`clampFiltersToScope`) — o servidor aplica esta função a cada
 *    pedido, nunca o que o navegador manda;
 *  - a SANITIZAÇÃO (`sanitizeForShare`): o visitante recebe indicadores já
 *    calculados, com chaves opacas no lugar dos ids e sem rotas internas.
 */

export const DASHBOARD_SHARE_TYPE = "INDICATORS_PROGRESS" as const;
export type DashboardShareType = typeof DASHBOARD_SHARE_TYPE;

export type ShareValidity = "NONE" | "24H" | "7D" | "30D" | "CUSTOM";
export type ShareScopeMode = "FIXED" | "DYNAMIC";
export type ShareRefreshMode = "REALTIME" | "INTERVAL" | "MANUAL";
export type ShareAccess = "PUBLIC" | "PASSWORD";
export type ShareLinkState = "ACTIVE" | "EXPIRED" | "REVOKED";

export const SHARE_REFRESH_INTERVALS = [15, 30, 60] as const;
export type ShareRefreshInterval = typeof SHARE_REFRESH_INTERVALS[number];
/**
 * "Tempo real" é polling curto. Não há WebSocket nem SSE no projeto, e o
 * Painel já é orientado a consulta + recálculo: 15 s é o intervalo mais
 * curto oferecido, e o servidor recalcula tudo a cada consulta.
 */
export const REALTIME_INTERVAL_SECONDS = 15;
export const MAX_CUSTOM_VALIDITY_DAYS = 365;
export const MIN_SHARE_PASSWORD_LENGTH = 8;

export const SHARE_VALIDITY_LABELS: Readonly<Record<ShareValidity, string>> = {
  NONE: "Sem expiração", "24H": "24 horas", "7D": "7 dias", "30D": "30 dias", CUSTOM: "Personalizada"
};
export const SHARE_REFRESH_LABELS: Readonly<Record<ShareRefreshMode, string>> = {
  REALTIME: "Em tempo real (a cada 15 s)", INTERVAL: "Automática a cada X segundos", MANUAL: "Manual"
};
export const SHARE_STATE_LABELS: Readonly<Record<ShareLinkState, string>> = {
  ACTIVE: "ATIVO", EXPIRED: "EXPIRADO", REVOKED: "REVOGADO"
};

/** Mensagens exibidas ao visitante — as do comando, literalmente. */
export const SHARE_MESSAGES = {
  EXPIRED: "Este link expirou.",
  REVOKED: "Este link foi revogado.",
  NOT_FOUND: "Link inválido ou inexistente.",
  UNAVAILABLE: "Este link não está mais disponível.",
  PASSWORD_REQUIRED: "Este painel é protegido por senha.",
  PASSWORD_INVALID: "Senha incorreta.",
  LOCKED: "Muitas tentativas de senha. Aguarde alguns minutos e tente novamente."
} as const;

/**
 * O escopo gravado no link. `clientId` é obrigatório: todo repositório de
 * dados filtra pelo Cliente ativo, e um link sem Cliente não teria recorte.
 */
export interface DashboardShareScope {
  readonly clientId: string;
  readonly projectId: string | null;
  readonly unitAreaId: string | null;
  readonly inspectorProfessionalId: string | null;
  readonly from: string | null;
  readonly to: string | null;
  /** FIXO: o visitante vê exatamente o recorte. DINÂMICO: pode estreitar dentro dele. */
  readonly mode: ShareScopeMode;
  /**
   * Mostrar o ranking por Inspetor. Exibe NOMES de pessoas e o ritmo de
   * trabalho de cada uma a quem tiver o link — por isso nasce desligado e
   * é decisão consciente de quem cria.
   */
  readonly showInspectors: boolean;
}

export interface DashboardShareLink {
  readonly id: string;
  /** 43 caracteres base64url (32 bytes aleatórios). Nunca vai para log nem auditoria. */
  readonly token: string;
  readonly clientId: string;
  readonly name: string;
  readonly description: string | null;
  readonly dashboardType: DashboardShareType;
  readonly scope: DashboardShareScope;
  readonly refreshMode: ShareRefreshMode;
  readonly refreshIntervalSeconds: ShareRefreshInterval;
  readonly access: ShareAccess;
  /** PBKDF2 (`password-hash.ts`). Nunca sai do servidor. */
  readonly passwordHash: string | null;
  readonly expiresAt: string | null;
  readonly revokedAt: string | null;
  readonly revokedBy: string | null;
  readonly revokeReason: string | null;
  readonly createdBy: string;
  readonly createdByName: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly updatedBy: string;
  readonly lastAccessAt: string | null;
  readonly accessCount: number;
}

export type DashboardShareEventType = "CREATED" | "UPDATED" | "RENEWED" | "REVOKED" | "ACCESSED";

/** Nome do evento na auditoria — os do comando. */
export const SHARE_AUDIT_EVENT: Readonly<Record<DashboardShareEventType, string>> = {
  CREATED: "DASHBOARD_SHARE_LINK_CREATED",
  UPDATED: "DASHBOARD_SHARE_LINK_UPDATED",
  RENEWED: "DASHBOARD_SHARE_LINK_UPDATED",
  REVOKED: "DASHBOARD_SHARE_LINK_REVOKED",
  ACCESSED: "DASHBOARD_SHARE_LINK_ACCESSED"
};

export interface DashboardShareEvent {
  readonly id: string;
  readonly linkId: string;
  readonly event: DashboardShareEventType;
  readonly at: string;
  /** Quem agiu; "public" no acesso pelo link. */
  readonly userId: string;
  readonly userName: string | null;
  readonly detail: Readonly<Record<string, unknown>>;
}

// ─────────────────────────────────────────────────────────────── token

const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  const b64 = typeof btoa === "function" ? btoa(binary) : Buffer.from(bytes).toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** 32 bytes do gerador criptográfico — 256 bits, sem relação com id nenhum. */
export function generateShareToken(): string {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

/** Recusa na porta o que nem tem formato de token — não consulta o banco por lixo. */
export function isWellFormedShareToken(token: unknown): token is string {
  return typeof token === "string" && TOKEN_PATTERN.test(token);
}

export function sharePath(token: string): string {
  return `/share/dashboard/${token}`;
}

// ─────────────────────────────────────────────────────────────── validade e estado

const DAY_MS = 86_400_000;

/** Revogado vence expirado: é ato deliberado, e a mensagem deve dizer isso. */
export function shareLinkState(link: Pick<DashboardShareLink, "revokedAt" | "expiresAt">, now: Date = new Date()): ShareLinkState {
  if (link.revokedAt) return "REVOKED";
  if (link.expiresAt && Date.parse(link.expiresAt) <= now.getTime()) return "EXPIRED";
  return "ACTIVE";
}

/**
 * Data de expiração a partir da escolha. Personalizada precisa estar no
 * futuro e a no máximo um ano: um link "personalizado" para 2090 é, na
 * prática, um link sem expiração que ninguém escolheu.
 */
export function expiresAtFor(validity: ShareValidity, now: Date, customExpiresAt?: string | null): { expiresAt: string | null; error: string | null } {
  switch (validity) {
    case "NONE": return { expiresAt: null, error: null };
    case "24H": return { expiresAt: new Date(now.getTime() + DAY_MS).toISOString(), error: null };
    case "7D": return { expiresAt: new Date(now.getTime() + 7 * DAY_MS).toISOString(), error: null };
    case "30D": return { expiresAt: new Date(now.getTime() + 30 * DAY_MS).toISOString(), error: null };
    case "CUSTOM": {
      const at = customExpiresAt ? Date.parse(customExpiresAt) : NaN;
      if (!Number.isFinite(at)) return { expiresAt: null, error: "Informe a data e a hora de expiração." };
      if (at <= now.getTime()) return { expiresAt: null, error: "A expiração personalizada precisa estar no futuro." };
      if (at > now.getTime() + MAX_CUSTOM_VALIDITY_DAYS * DAY_MS) return { expiresAt: null, error: `A expiração personalizada pode ser de no máximo ${MAX_CUSTOM_VALIDITY_DAYS} dias.` };
      return { expiresAt: new Date(at).toISOString(), error: null };
    }
    default: return { expiresAt: null, error: "Validade inválida." };
  }
}

/** Intervalo efetivo de atualização automática (0 = manual). */
export function effectiveRefreshSeconds(link: Pick<DashboardShareLink, "refreshMode" | "refreshIntervalSeconds">): number {
  if (link.refreshMode === "MANUAL") return 0;
  if (link.refreshMode === "REALTIME") return REALTIME_INTERVAL_SECONDS;
  return link.refreshIntervalSeconds;
}

// ─────────────────────────────────────────────────────────────── entrada

export interface CreateShareLinkInput {
  readonly name: string;
  readonly description?: string | null;
  readonly validity: ShareValidity;
  readonly customExpiresAt?: string | null;
  readonly scope: {
    readonly projectId?: string | null;
    readonly unitAreaId?: string | null;
    readonly inspectorProfessionalId?: string | null;
    readonly from?: string | null;
    readonly to?: string | null;
    readonly mode: ShareScopeMode;
    readonly showInspectors?: boolean;
  };
  readonly refreshMode: ShareRefreshMode;
  readonly refreshIntervalSeconds?: number;
  readonly access: ShareAccess;
  readonly password?: string | null;
}

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const isValidDay = (value: string): boolean => DAY_PATTERN.test(value) && Number.isFinite(Date.parse(`${value}T00:00:00Z`));

/** Erros de preenchimento, em português, prontos para a tela. Lista vazia = válido. */
export function validateShareInput(input: CreateShareLinkInput): string[] {
  const errors: string[] = [];
  const name = String(input.name ?? "").trim();
  if (name.length < 3) errors.push("Informe o nome do link (mínimo 3 caracteres).");
  if (name.length > 120) errors.push("O nome do link pode ter no máximo 120 caracteres.");
  if (input.description && String(input.description).length > 500) errors.push("A descrição pode ter no máximo 500 caracteres.");
  if (!(["NONE", "24H", "7D", "30D", "CUSTOM"] as const).includes(input.validity)) errors.push("Escolha a validade do link.");
  if (!(["FIXED", "DYNAMIC"] as const).includes(input.scope?.mode)) errors.push("Escolha o escopo: fixo ou dinâmico.");
  if (!(["REALTIME", "INTERVAL", "MANUAL"] as const).includes(input.refreshMode)) errors.push("Escolha o modo de atualização.");
  if (input.refreshMode === "INTERVAL" && !SHARE_REFRESH_INTERVALS.includes(Number(input.refreshIntervalSeconds) as ShareRefreshInterval)) {
    errors.push("O intervalo de atualização deve ser 15, 30 ou 60 segundos.");
  }
  if (!(["PUBLIC", "PASSWORD"] as const).includes(input.access)) errors.push("Escolha o tipo de acesso.");
  if (input.access === "PASSWORD" && String(input.password ?? "").length < MIN_SHARE_PASSWORD_LENGTH) {
    errors.push(`A senha do link precisa ter pelo menos ${MIN_SHARE_PASSWORD_LENGTH} caracteres.`);
  }
  const from = input.scope?.from || null;
  const to = input.scope?.to || null;
  if (from && !isValidDay(from)) errors.push("Data inicial do período inválida.");
  if (to && !isValidDay(to)) errors.push("Data final do período inválida.");
  if (from && to && isValidDay(from) && isValidDay(to) && from > to) errors.push("A data inicial do período não pode ser posterior à final.");
  return errors;
}

// ─────────────────────────────────────────────────────────────── escopo

/** O universo que o Cliente do link realmente tem — lido no servidor, a cada pedido. */
export interface ShareUniverse {
  readonly projectIds: ReadonlySet<string>;
  /** unitAreaId → projectId */
  readonly unitAreas: ReadonlyMap<string, string>;
  readonly inspectorIds: ReadonlySet<string>;
}

const maxDay = (a: string | null, b: string | null): string | null => (a && b ? (a > b ? a : b) : a || b);
const minDay = (a: string | null, b: string | null): string | null => (a && b ? (a < b ? a : b) : a || b);

/**
 * O recorte que vale para o visitante. FIXO ignora o pedido. DINÂMICO aceita
 * só o que estreita o escopo gravado: um link de Projeto A nunca troca para
 * o Projeto B, uma Unidade de outro Projeto é descartada e o período nunca
 * sai dos limites gravados. O que não passar na conferência é simplesmente
 * ignorado — o visitante vê o escopo, nunca mais que ele.
 */
export function clampFiltersToScope(scope: DashboardShareScope, requested: DashboardFilters | null | undefined, universe: ShareUniverse): DashboardFilters {
  const fixed: DashboardFilters = {
    projectId: scope.projectId,
    unitAreaId: scope.unitAreaId,
    inspectorProfessionalId: scope.inspectorProfessionalId,
    from: scope.from,
    to: scope.to
  };
  if (scope.mode === "FIXED" || !requested) return stripEmpty(fixed);

  let projectId = scope.projectId;
  if (!projectId && requested.projectId && universe.projectIds.has(String(requested.projectId))) projectId = String(requested.projectId);

  let unitAreaId = scope.unitAreaId;
  if (!unitAreaId && requested.unitAreaId) {
    const unitProject = universe.unitAreas.get(String(requested.unitAreaId));
    if (unitProject !== undefined && (!projectId || unitProject === projectId)) unitAreaId = String(requested.unitAreaId);
  }

  let inspectorProfessionalId = scope.inspectorProfessionalId;
  if (!inspectorProfessionalId && scope.showInspectors && requested.inspectorProfessionalId && universe.inspectorIds.has(String(requested.inspectorProfessionalId))) {
    inspectorProfessionalId = String(requested.inspectorProfessionalId);
  }

  const reqFrom = requested.from && isValidDay(String(requested.from)) ? String(requested.from) : null;
  const reqTo = requested.to && isValidDay(String(requested.to)) ? String(requested.to) : null;
  let from = maxDay(scope.from, reqFrom);
  let to = minDay(scope.to, reqTo);
  if (from && to && from > to) { from = scope.from; to = scope.to; }

  return stripEmpty({ projectId, unitAreaId, inspectorProfessionalId, from, to });
}

function stripEmpty(filters: DashboardFilters): DashboardFilters {
  const out: DashboardFilters = {};
  if (filters.projectId) out.projectId = filters.projectId;
  if (filters.unitAreaId) out.unitAreaId = filters.unitAreaId;
  if (filters.inspectorProfessionalId) out.inspectorProfessionalId = filters.inspectorProfessionalId;
  if (filters.from) out.from = filters.from;
  if (filters.to) out.to = filters.to;
  return out;
}

// ─────────────────────────────────────────────────────────────── sanitização

/** id interno → chave opaca estável por link (sem o id, não há como voltar). */
export type OpaqueKeyOf = (kind: "project" | "unit" | "inspector" | "system", id: string) => string;

/**
 * O que o visitante recebe. Mesma forma que o Painel interno já sabe
 * desenhar (`DashboardIndicators` / `InspectorPerformanceReport`), mas:
 *  - `ProgressBar.id`, `professionalId` e `systemId` viram chaves opacas;
 *  - horas apontadas no RDO saem zeradas (dado trabalhista, não indicador);
 *  - sem o ranking quando o link não o autoriza;
 *  - nada de rota, atalho, token, id de Cliente ou de cadastro.
 */
export interface SanitizedDashboard {
  readonly data: DashboardIndicators;
  readonly performance: InspectorPerformanceReport | null;
}

export function sanitizeForShare(data: DashboardIndicators, performance: InspectorPerformanceReport | null, keyOf: OpaqueKeyOf, showInspectors: boolean): SanitizedDashboard {
  const bars = (list: readonly ProgressBar[], kind: "unit" | "system"): ProgressBar[] =>
    list.map(b => ({ id: keyOf(kind, b.id), label: b.label, percent: b.percent, completed: b.completed, total: b.total }));
  const safeData: DashboardIndicators = {
    assets: data.assets,
    completedInspections: data.completedInspections,
    totalInspections: data.totalInspections,
    plannedProgress: data.plannedProgress,
    realProgress: data.realProgress,
    deviation: data.deviation,
    issuedReports: data.issuedReports,
    openRecommendations: data.openRecommendations,
    curve: { hasData: data.curve.hasData, points: data.curve.points.map(p => ({ key: p.key, label: p.label, planned: p.planned, real: p.real, executed: p.executed })) },
    status: { hasData: data.status.hasData, total: data.status.total, slices: data.status.slices.map(s => ({ ...s })) },
    unitProgress: { hasData: data.unitProgress.hasData, bars: bars(data.unitProgress.bars, "unit") },
    systemProgress: { hasData: data.systemProgress.hasData, bars: bars(data.systemProgress.bars, "system") }
  };
  if (!showInspectors || !performance) return { data: safeData, performance: null };
  const person = (p: InspectorPerformance): InspectorPerformance => ({
    ...p,
    professionalId: keyOf("inspector", p.professionalId),
    dailyRate: { ...p.dailyRate, hoursWorked: 0 },
    systems: p.systems.map(s => ({ ...s, systemId: keyOf("system", s.systemId), dailyRate: { ...s.dailyRate, hoursWorked: 0 } }))
  });
  return {
    data: safeData,
    performance: { hasData: performance.hasData, distinctLinesInScope: performance.distinctLinesInScope, overlapping: performance.overlapping, inspectors: performance.inspectors.map(person) }
  };
}

/** Chave opaca: SHA-256 de (id do link + tipo + id), 16 hex. */
export async function opaqueKey(linkId: string, kind: string, id: string): Promise<string> {
  const bytes = new TextEncoder().encode(`${linkId}:${kind}:${id}`);
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest)).slice(0, 8).map(b => b.toString(16).padStart(2, "0")).join("");
}
